/**
 * Unit Detail Modal Component
 * Displays detailed information about a selected unit
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Bed, Bath, Maximize2, Eye, Calendar, ArrowRight } from 'lucide-react';
import { PropertyGallery } from './PropertyGallery';
import { FloorPlanViewer } from './FloorPlanViewer';
import { Matterport3DViewer } from './Matterport3DViewer';
import { Kuula3DViewer } from './Kuula3DViewer';
import { PriceOnRequestCard } from './PriceOnRequestCard';
import { UnitLocationMap } from './UnitLocationMap';
import type { PropertyUnit } from '@/lib/units';
import type { Tables } from '@/integrations/supabase/types';

// Extended type that includes floor_plan_images from database
type UnitWithFloorPlans = PropertyUnit & {
  floor_plan_images?: string[] | null;
};
import { formatCurrency, formatFloorDisplay, getStatusBadgeClasses } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';

interface UnitDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit: PropertyUnit | null;
  onRegisterClick: () => void;
  onConsultClick: () => void;
}

export function UnitDetailModal({
  open,
  onOpenChange,
  unit,
  onRegisterClick,
  onConsultClick,
}: UnitDetailModalProps) {
  const navigate = useNavigate();

  if (!unit) {
    return null;
  }

  const handleViewFullDetails = () => {
    onOpenChange(false);
    navigate(`/residences/${unit.unit_number}`);
  };

  const formatArea = (area: number | null) => {
    if (!area) return 'N/A';
    return `${area.toLocaleString()} sq.ft`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold">
            Unit {unit.unit_number}
          </DialogTitle>
          <DialogDescription>
            Detailed information about unit {unit.unit_number}, including specifications, pricing, and availability.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Unit Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="default" className="bg-brand-teal text-white">
              {unit.category}
            </Badge>
            <Badge 
              className={getStatusBadgeClasses(unit.availability)}
            >
              {unit.availability.charAt(0).toUpperCase() + unit.availability.slice(1)}
            </Badge>
            <Badge variant="outline">
              {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
            </Badge>
            <Badge variant="outline">
              {formatFloorDisplay(unit.floor)}
            </Badge>
          </div>

          {/* Unit Location on Site Plan */}
          <UnitLocationMap 
            unitNumber={unit.unit_number}
            compact
            showTitle
          />

          {/* Unit Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Bed className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Bedrooms</div>
                <div className="font-semibold">{unit.bedrooms}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bath className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Bathrooms</div>
                <div className="font-semibold">{unit.bathrooms}</div>
              </div>
            </div>
            {unit.powder_rooms > 0 && (
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Powder Rooms</div>
                  <div className="font-semibold">{unit.powder_rooms}</div>
                </div>
              </div>
            )}
            {unit.parking_spaces !== null && unit.parking_spaces > 0 && (
              <div className="flex items-center gap-2">
                <Maximize2 className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Parking</div>
                  <div className="font-semibold">{unit.parking_spaces} space{unit.parking_spaces > 1 ? 's' : ''}</div>
                </div>
              </div>
            )}
            {unit.patio !== null && unit.patio > 0 && (
              <div className="flex items-center gap-2">
                <Maximize2 className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Patio</div>
                  <div className="font-semibold">{formatArea(unit.patio)}</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Maximize2 className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Total Area</div>
                <div className="font-semibold">{formatArea(unit.area_total)}</div>
              </div>
            </div>
          </div>

          {/* Area Breakdown */}
          {(unit.area_internal || unit.area_external_covered || unit.area_external_uncovered) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
              {unit.area_internal && (
                <div>
                  <div className="text-sm text-muted-foreground">Internal</div>
                  <div className="font-semibold">{formatArea(unit.area_internal)}</div>
                </div>
              )}
              {unit.area_external_covered && (
                <div>
                  <div className="text-sm text-muted-foreground">External Covered</div>
                  <div className="font-semibold">{formatArea(unit.area_external_covered)}</div>
                </div>
              )}
              {unit.area_external_uncovered && (
                <div>
                  <div className="text-sm text-muted-foreground">External Uncovered</div>
                  <div className="font-semibold">{formatArea(unit.area_external_uncovered)}</div>
                </div>
              )}
            </div>
          )}

          {/* Price */}
          {unit.show_price && unit.price ? (
            <div className="p-4 bg-brand-teal/10 rounded-lg border border-brand-teal">
              <div className="text-sm text-muted-foreground mb-1">Price</div>
              <div className="text-3xl font-bold text-brand-teal">
                {formatCurrency(unit.price)}
              </div>
            </div>
          ) : (
            <PriceOnRequestCard
              variant="expanded"
              onRequestClick={() => {
                trackEvent('price_inquiry', {
                  source: 'unit_detail_modal',
                  unit_number: unit.unit_number,
                  inquiry_type: 'hidden_price'
                });
                onRegisterClick();
              }}
            />
          )}

          {/* Images Gallery */}
          {unit.images && unit.images.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Unit Images</h3>
              <PropertyGallery images={unit.images} propertyName={unit.unit_number} />
            </div>
          )}

          {/* Floor Plans */}
          {(unit as UnitWithFloorPlans).floor_plan_images && (unit as UnitWithFloorPlans).floor_plan_images && (unit as UnitWithFloorPlans).floor_plan_images!.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">
                Floor {(unit as UnitWithFloorPlans).floor_plan_images!.length > 1 ? 'Plans' : 'Plan'}
              </h3>
              <FloorPlanViewer 
                floorPlanImages={(unit as UnitWithFloorPlans).floor_plan_images!} 
                propertyName={unit.unit_number} 
              />
            </div>
          )}

          {/* 3D Virtual Tour - Kuula or Matterport */}
          {(unit.kuula_url || unit.matterport_url) && (
            <div>
              <h3 className="text-xl font-semibold mb-4">3D Virtual Tour</h3>
              {unit.kuula_url ? (
                <Kuula3DViewer kuulaUrl={unit.kuula_url} unitNumber={unit.unit_number} />
              ) : unit.matterport_url ? (
                <Matterport3DViewer matterportUrl={unit.matterport_url} propertyName={unit.unit_number} />
              ) : null}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-4 border-t">
            <Button
              size="lg"
              className="bg-brand-teal hover:bg-brand-navy text-white flex-1"
              onClick={onRegisterClick}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Register Interest
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={onConsultClick}
            >
              <Eye className="w-5 h-5 mr-2" />
              Schedule Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleViewFullDetails}
            >
              View Full Details
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

